import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Lock, Check, Sun, Moon, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const { language, toggleLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setValidatingToken(false);
        return;
      }
      
      try {
        await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setTokenValid(true);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setValidatingToken(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError(language === 'fr' ? 'Le mot de passe doit contenir au moins 6 caractères' : 'Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'fr' ? 'Une erreur est survenue' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validatingToken) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-[#3272F1] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Invalid or no token
  if (!token || !tokenValid) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'} flex flex-col`} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
        {isDark && (
          <div className="fixed inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3272F1]/10 rounded-full blur-3xl"></div>
          </div>
        )}
        
        <header className="relative z-10 p-6 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_exitcall/artifacts/v6rf7bvn_logo.svg" 
              alt="HICALL" 
              className="h-10"
            />
          </Link>
        </header>
        
        <main className="relative z-10 flex-1 flex items-center justify-center p-4">
          <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-lg'} rounded-2xl border p-8 max-w-md w-full text-center`}>
            <div className="w-14 h-14 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
              {language === 'fr' ? 'Lien invalide ou expiré' : 'Invalid or expired link'}
            </h1>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
              {language === 'fr' 
                ? 'Ce lien de réinitialisation n\'est plus valide. Veuillez en demander un nouveau.'
                : 'This reset link is no longer valid. Please request a new one.'}
            </p>
            <Link to="/forgot-password">
              <Button className="bg-[#3272F1] hover:bg-[#2560d9] text-white px-6 py-3 rounded-xl">
                {language === 'fr' ? 'Demander un nouveau lien' : 'Request a new link'}
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'} flex flex-col`} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Background effects */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3272F1]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-3xl"></div>
        </div>
      )}

      {/* Header */}
      <header className="relative z-10 p-6 flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_exitcall/artifacts/v6rf7bvn_logo.svg" 
            alt="HICALL" 
            className="h-10"
          />
        </Link>
        <div className="flex items-center gap-2">
          <button 
            onClick={toggleTheme}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
            data-testid="theme-toggle"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button 
            onClick={toggleLanguage}
            className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} text-sm transition-colors`}
            data-testid="lang-toggle"
          >
            {language.toUpperCase()}
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-lg'} rounded-2xl border p-8`}>
            
            {!success ? (
              <>
                <div className="text-center mb-8">
                  <div className="w-14 h-14 bg-[#3272F1] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                    {language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
                  </h1>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-600'}>
                    {language === 'fr' 
                      ? 'Créez votre nouveau mot de passe'
                      : 'Create your new password'}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-sm" data-testid="reset-error">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                      {language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className={`${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#3272F1] focus:ring-[#3272F1]`}
                      data-testid="password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                      {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className={`${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#3272F1] focus:ring-[#3272F1]`}
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#3272F1] hover:bg-[#2560d9] text-white py-6 rounded-xl font-semibold" 
                    disabled={loading}
                    data-testid="reset-submit"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset password'
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className="w-14 h-14 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-green-500" />
                </div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {language === 'fr' ? 'Mot de passe réinitialisé !' : 'Password reset!'}
                </h2>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
                  {language === 'fr' 
                    ? 'Vous allez être redirigé vers la page de connexion...'
                    : 'You will be redirected to the login page...'}
                </p>
                <div className="w-6 h-6 border-2 border-[#3272F1] border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            )}

            {/* Back to login link */}
            {!success && (
              <div className="mt-6 text-center">
                <Link 
                  to="/login" 
                  className={`inline-flex items-center gap-2 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
